import React from 'react';
import { Box, Button, Chip, Divider, Paper, Typography } from '@mui/material';
import type { CompositeAlertDetailView, ReviewedCompositePatternView } from '../../domain/incidents/compositeAlertPresentation';

function memberBuyerCopy(member: CompositeAlertDetailView['terms'][number]['membership'][number]): string {
  const parts = [
    member.occurredAt,
    member.policyName,
  ].filter(Boolean);
  return parts.join(' · ') || 'Source evidence recorded';
}

export function CompositeAlertDetail({
  detail,
  onClose,
}: {
  detail: CompositeAlertDetailView;
  onClose?: () => void;
}) {
  return (
    <Paper sx={{ p: 2, mt: 2 }} data-testid="composite-alert-detail">
      <Box display="flex" justifyContent="space-between" alignItems="center" gap={2} flexWrap="wrap">
        <Box>
          <Chip label={detail.alertClass} color="info" size="small" data-testid="composite-alert-detail-class" />
          <Typography variant="h6" component="h2" sx={{ mt: 1 }} data-testid="composite-alert-detail-rule-name">
            {detail.ruleName}
          </Typography>
        </Box>
        {onClose ? (
          <Button size="small" onClick={onClose} data-testid="composite-alert-detail-close">
            Close
          </Button>
        ) : null}
      </Box>
      <Typography variant="body1" sx={{ mt: 1 }} data-testid="composite-alert-detail-sentence">
        {detail.readableSentence || 'Composite Alert facts are unavailable.'}
      </Typography>
      <Box sx={{ mt: 2 }} data-testid="composite-alert-detail-human-facts">
        <Typography variant="body2"><strong>Operator:</strong> {detail.operator}</Typography>
        <Typography variant="body2" data-testid="composite-alert-detail-location">
          <strong>Location:</strong> {detail.location}
        </Typography>
        <Typography variant="body2" data-testid="composite-alert-detail-window">
          <strong>Window:</strong> {detail.windowLabel}
        </Typography>
        <Typography variant="body2" data-testid="composite-alert-detail-scope">
          <strong>Relationship:</strong> {detail.scopeLabel}
        </Typography>
        <Typography variant="body2"><strong>Cooldown:</strong> {detail.cooldownLabel}</Typography>
        <Typography variant="body2" data-testid="composite-alert-detail-triggered">
          <strong>Triggered:</strong> {detail.triggeredAt}
        </Typography>
        <Typography variant="body2" data-testid="composite-alert-detail-severity">
          <strong>Severity:</strong> {detail.severity}
        </Typography>
        <Typography variant="body2" data-testid="composite-alert-detail-delivery">
          <strong>Delivery:</strong> {detail.deliveryLabel}
        </Typography>
        {detail.deliveryCopy ? (
          <Typography variant="caption" color="text.secondary">{detail.deliveryCopy}</Typography>
        ) : null}
      </Box>
      <Divider sx={{ my: 2 }} />
      <Typography variant="subtitle2">Participating conditions</Typography>
      {detail.terms.map((term) => (
        <Box key={term.termId} sx={{ mt: 1 }} data-testid="composite-alert-detail-term">
          <Typography variant="body2" data-testid="composite-alert-detail-term-label">{term.conditionLabel}</Typography>
          {term.sourcePolicyName ? (
            <Typography variant="caption" display="block" data-testid="composite-alert-detail-source-policy">
              {term.sourcePolicyName}
            </Typography>
          ) : null}
          <Typography variant="caption" data-testid="composite-alert-detail-required">
            Required: {term.requiredCount ?? '—'}
          </Typography>
          {' · '}
          <Typography variant="caption" data-testid="composite-alert-detail-observed">
            Observed: {term.observedCount ?? '—'}
          </Typography>
          <Box sx={{ pl: 1 }} data-testid="composite-alert-detail-membership">
            {term.membership.map((member, index) => (
              <Typography key={`${term.termId}-member-${index}`} variant="caption" display="block">
                {memberBuyerCopy(member)}
              </Typography>
            ))}
          </Box>
        </Box>
      ))}
      {detail.evidencePresent ? (
        <Typography variant="body2" sx={{ mt: 2 }} data-testid="composite-alert-detail-evidence">
          Source evidence is available for the participating conditions.
        </Typography>
      ) : null}
    </Paper>
  );
}

export default CompositeAlertDetail;

export function ReviewedCompositePatternDetail({
  detail,
  onClose,
}: {
  detail: ReviewedCompositePatternView;
  onClose?: () => void;
}) {
  return (
    <Paper sx={{ p: 2, mt: 2 }} data-testid="reviewed-composite-pattern">
      <Box display="flex" justifyContent="space-between" alignItems="center" gap={2} flexWrap="wrap">
        <Box>
          <Chip label="Pattern" color="warning" size="small" data-testid="reviewed-composite-pattern-class" />
          <Typography variant="h6" component="h2" sx={{ mt: 1 }} data-testid="reviewed-composite-pattern-headline">
            {detail.headline}
          </Typography>
        </Box>
        {onClose ? (
          <Button size="small" onClick={onClose} data-testid="reviewed-composite-pattern-close">
            Close
          </Button>
        ) : null}
      </Box>
      <Typography variant="body2" sx={{ mt: 1 }} data-testid="reviewed-composite-pattern-location">
        <strong>Location:</strong> {detail.location}
      </Typography>
      <Typography variant="body2" data-testid="reviewed-composite-pattern-count">
        {detail.occurrenceCount} occurrences
      </Typography>
      <Typography variant="body2" sx={{ mt: 1 }} data-testid="reviewed-composite-pattern-evidence">
        {detail.evidenceCopy}
      </Typography>
      {detail.controlPath ? (
        <Button
          size="small"
          sx={{ mt: 1.5, fontWeight: 700, textTransform: 'none' }}
          href={detail.controlPath}
          data-testid="reviewed-composite-record-control"
        >
          Record control
        </Button>
      ) : null}
      <Divider sx={{ my: 2 }} />
      {detail.members.map((member) => (
        <Box key={member.incidentId} sx={{ mt: 1 }} data-testid="reviewed-composite-pattern-member">
          <Typography variant="subtitle2">{member.occurredAt || 'Occurrence'}</Typography>
          {member.terms.map((term) => (
            <Box key={`${member.incidentId}-${term.termId}`} sx={{ pl: 1 }} data-testid="reviewed-composite-pattern-term">
              <Typography variant="body2">{term.conditionLabel}</Typography>
              {term.membership.map((source, index) => (
                <Typography key={`${term.termId}-${index}`} variant="caption" display="block">
                  {memberBuyerCopy(source)}
                </Typography>
              ))}
            </Box>
          ))}
        </Box>
      ))}
    </Paper>
  );
}
